import { RightEnum } from "@finway-group/shared/lib/models"
import { Badge, Divider, Layout, Menu } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Link, matchPath, useLocation } from "react-router-dom"
import { Icon } from "~Components/icon"
import { CashFlowPlanningIcon } from "~Components/icons/CashFlowPlanningIcon"
import { CategorySettingsIcon } from "~Components/icons/CategorySettingsIcon"
import { DashboardLiquidityIcon } from "~Components/icons/DashboardLiquidityIcon"
import { ActionMenuSidebar } from "~Components/layout/partials/navbar/ActionMenuSidebar"
import { SystemInfo } from "~Components/layout/partials/SystemInfo"
import { BetaTag } from "~Components/tags/BetaTag"
import { HELP_CENTER_LINK } from "~Shared/config/consts"
import { useAuth, useIsAiReportingEnabled } from "~Shared/hooks/auth.hooks"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useExpenseCounts } from "~Shared/hooks/expense.hooks"
import { useIsFreeVersion, useIsPurchaseOrderEnabled } from "~Shared/hooks/featureFlags.hooks"
import { useLoggedInEmployeeProfile } from "~Shared/hooks/user.hooks"
import { CompanyScopedRoutePathEnum, RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { AuthnService, AuthzService } from "~Shared/services"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"
import { getOpenInOidcClientAppUrl } from "~Shared/utils/oidcClientAppLink.utils"
import { generatePath } from "~Shared/utils/routing.utils"
import { openWindowInNewTab } from "~Shared/utils/window.utils"

const { Sider } = Layout

const showLiquidity = true // Set to true to show the WIP liquidity feature. TO BE REMOVED WHEN THE FEATURE IS READY.

interface SidebarProps {
    onMobileDevice: boolean
    showMobileNav: boolean
}
export const Sidebar = ({ onMobileDevice, showMobileNav }: SidebarProps) => {
    const { t } = useTranslation()
    const location = useLocation()

    const openAiReportingInNewTab = async (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault()

        await AuthnService.silentRefreshWithoutLogout()

        openWindowInNewTab({ url: getOpenInOidcClientAppUrl() })
    }
    const [badgeValue, setBadgeValue] = useStateIfMounted(0)

    const { isAuthenticated } = useAuth()
    const isPurchaseOrderEnabled = useIsPurchaseOrderEnabled()
    const isAiReportingEnabled = useIsAiReportingEnabled()

    const expenseCounts = useExpenseCounts()
    const company = useCompany()
    const isFreeVersion = useIsFreeVersion()
    const loggedInUser = useLoggedInEmployeeProfile()

    const matchedPattern = [...Object.values(CompanyScopedRoutePathEnum), ...Object.values(RoutePathEnum)].find((pattern) =>
        matchPath(location.pathname, {
            path: pattern,
            exact: false,
            strict: false,
        }),
    )
    const activeMenuItem = matchedPattern ?? CompanyScopedRoutePathEnum.EXPENSES

    const asideStyles = {
        left: !onMobileDevice || showMobileNav ? "0px" : "-200px",
        transition: "left .5s",
    }

    useEffect(() => {
        setBadgeValue(expenseCounts.toDo + expenseCounts.review + expenseCounts.payExport)
    }, [expenseCounts.toDo, expenseCounts.review, expenseCounts.payExport])

    if (!isAuthenticated || !loggedInUser) {
        return <React.Fragment />
    }

    return (
        <Sider className="z-50 bg-body h-full w-200 sm:w-3/12 md:w-3/12 lg:w-2/12 pt-20 sm:pt-34 md:pt-34 pl-16 fixed lg:relative" style={asideStyles}>
            {isFreeVersion ? (
                <Menu className="fixed" selectedKeys={[activeMenuItem]}>
                    <div className="overflow-y-auto" style={{ height: "calc(100vh - 90px)", maxWidth: "184px" }}>
                        <Menu.Item key={CompanyScopedRoutePathEnum.LM_CASHFLOW_PLANNING}>
                            <Link to={generatePath(CompanyScopedRoutePathEnum.LM_CASHFLOW_PLANNING, {})}>
                                <CashFlowPlanningIcon />
                                <span>{t("nav:cashflow_planning")}</span>
                            </Link>
                        </Menu.Item>
                        <Menu.Item key={CompanyScopedRoutePathEnum.LM_DASHBOARD}>
                            <Link to={generatePath(CompanyScopedRoutePathEnum.LM_DASHBOARD, {})}>
                                <DashboardLiquidityIcon />
                                <span>{t("nav:dashboard")}</span>
                            </Link>
                        </Menu.Item>
                        <Menu.Item key={CompanyScopedRoutePathEnum.LM_CATEGORIZATION}>
                            <Link to={generatePath(CompanyScopedRoutePathEnum.LM_CATEGORIZATION, {})}>
                                <CategorySettingsIcon />
                                <span>{t("nav:category_settings")}</span>
                            </Link>
                        </Menu.Item>
                        <Menu.Item key={CompanyScopedRoutePathEnum.TRANSACTIONS}>
                            <Link to={generatePath(CompanyScopedRoutePathEnum.TRANSACTIONS, {})}>
                                <Icon icon="transaction" />
                                <span>{t("nav:transactions")}</span>
                            </Link>
                        </Menu.Item>
                        <Menu.Item key={CompanyScopedRoutePathEnum.EMPLOYEES}>
                            <Link to={generatePath(CompanyScopedRoutePathEnum.EMPLOYEES, {})}>
                                <Icon icon="employee" />
                                <span>{t("nav:user_management")}</span>
                            </Link>
                        </Menu.Item>
                        <Menu.Item key="help" className="opacity-75">
                            <Divider className="mt-4 mb-4" />
                            <a href={HELP_CENTER_LINK} target="_blank" rel="noreferrer">
                                <Icon icon="help" />
                                <span>{t("nav:help_center")}</span>
                            </a>
                        </Menu.Item>
                    </div>
                </Menu>
            ) : (
                <Menu className="fixed" selectedKeys={[activeMenuItem]}>
                    <div className="overflow-y-auto" style={{ height: "calc(100vh - 90px)", maxWidth: "184px" }}>
                        <ActionMenuSidebar />
                        <Menu.Item data-testid="dashboardSideBar" key={CompanyScopedRoutePathEnum.EXPENSES}>
                            <Link to={generatePath(CompanyScopedRoutePathEnum.EXPENSES, {})}>
                                <Icon icon="request" />
                                <span>{t("nav:dashboard")}</span>
                                <Badge count={badgeValue} showZero={false} className="-mb-5" />
                            </Link>
                        </Menu.Item>
                        {isPurchaseOrderEnabled && (
                            <>
                                <Menu.Item data-testid="purchaseOrdersSideBar" key={CompanyScopedRoutePathEnum.PURCHASE_ORDERS}>
                                    <Link to={generatePath(CompanyScopedRoutePathEnum.PURCHASE_ORDERS, {})}>
                                        <Icon icon="purchase_orders" />
                                        <span>{t("nav:purchase_orders")}</span>
                                    </Link>
                                </Menu.Item>
                                <Menu.Item data-testid="deliveryNotesSideBar" key={CompanyScopedRoutePathEnum.DELIVERY_NOTES}>
                                    <Link to={generatePath(CompanyScopedRoutePathEnum.DELIVERY_NOTES, {})}>
                                        <Icon icon="truck" />
                                        <span>{t("nav:delivery_notes")}</span>
                                    </Link>
                                </Menu.Item>
                            </>
                        )}
                        {AuthzService.canLoggedInUserSeeAllExpenses() && (
                            <Menu.Item data-testid="oposListSideBar" key={CompanyScopedRoutePathEnum.OPOS_LIST}>
                                <Link to={generatePath(CompanyScopedRoutePathEnum.OPOS_LIST, {})}>
                                    <Icon icon="opos" />
                                    <span>{t("nav:opos_list")}</span>
                                </Link>
                            </Menu.Item>
                        )}

                        <Menu.Item data-testid="subscriptionsSideBar" key={CompanyScopedRoutePathEnum.SUBSCRIPTIONS}>
                            <Link to={generatePath(CompanyScopedRoutePathEnum.SUBSCRIPTIONS, {})}>
                                <Icon icon="subscription" />
                                <span>{t("nav:subscriptions")}</span>
                            </Link>
                        </Menu.Item>
                        {company.cardsEnabled && (
                            <Menu.Item data-testid="creditCardsSideBar" key={CompanyScopedRoutePathEnum.CARDS}>
                                <Link to={generatePath(CompanyScopedRoutePathEnum.CARDS, {})}>
                                    <Icon icon="credit_card" />
                                    <span>{t("nav:credit_card")}</span>
                                </Link>
                            </Menu.Item>
                        )}
                        {AuthzService.isRightGrantedForLoggedInUser(RightEnum.TRANSACTION__ALL__READ) && (
                            <Menu.Item data-testid="transactionsSideBar" key={CompanyScopedRoutePathEnum.TRANSACTIONS}>
                                <Link to={generatePath(CompanyScopedRoutePathEnum.TRANSACTIONS, {})}>
                                    <Icon icon="transaction" />
                                    <span>{t("nav:transactions")}</span>
                                </Link>
                            </Menu.Item>
                        )}
                        <Menu.Item data-testid="employeesSideBar" key={CompanyScopedRoutePathEnum.EMPLOYEES}>
                            <Link to={generatePath(CompanyScopedRoutePathEnum.EMPLOYEES, {})}>
                                <Icon icon="employee" />
                                <span>{t("nav:employees")}</span>
                            </Link>
                        </Menu.Item>
                        <Menu.Item data-testid="finway:sidebar:menuCostCenters-btn" key={CompanyScopedRoutePathEnum.COST_CENTERS}>
                            <Link to={generatePath(CompanyScopedRoutePathEnum.COST_CENTERS, {})}>
                                <Icon icon="cost_center" />
                                <span>{t("nav:cost_centers")}</span>
                            </Link>
                        </Menu.Item>
                        {AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__READ) && (
                            <Menu.Item data-testid="finway:sidebar:menuVendor-btn" key={CompanyScopedRoutePathEnum.VENDORS}>
                                <Link to={generatePath(CompanyScopedRoutePathEnum.VENDORS, {})}>
                                    <Icon icon="vendor" />
                                    <span>{t("nav:vendors")}</span>
                                </Link>
                            </Menu.Item>
                        )}
                        {AuthzService.isRightGrantedForLoggedInUser(RightEnum.WORKFLOW__ALL__READ) && (
                            <Menu.Item data-testid="finway:sidebar:menuWorkflow-btn" key={CompanyScopedRoutePathEnum.WORKFLOWS}>
                                <Link to={generatePath(CompanyScopedRoutePathEnum.WORKFLOWS, {})}>
                                    <Icon icon="workflow" />
                                    <span>{t("nav:workflows")}</span>
                                </Link>
                            </Menu.Item>
                        )}
                        {AuthzService.canReadLiquidity() && company.liquidityManagementEnabled && showLiquidity && (
                            <Menu.Item key={CompanyScopedRoutePathEnum.LM}>
                                <Link to={generatePath(CompanyScopedRoutePathEnum.LM, {})}>
                                    <Icon icon="liquidity" />
                                    <span>{t("nav:liquidity")}</span>
                                    <BetaTag />
                                </Link>
                            </Menu.Item>
                        )}
                        {AuthzService.isRightGrantedForLoggedInUser(RightEnum.REPORTING__ALL__READ) && (
                            <Menu.Item key={CompanyScopedRoutePathEnum.REPORTS}>
                                <Link to={generatePath(CompanyScopedRoutePathEnum.REPORTS, {})}>
                                    <Icon icon="report" />
                                    <span>{t("nav:reporting")}</span>
                                </Link>
                            </Menu.Item>
                        )}

                        {isAiReportingEnabled && AuthzService.isRightGrantedForLoggedInUser(RightEnum.REPORTING__ALL__READ) && (
                            <Menu.Item key="open-in-oidc-client-app">
                                <a href={getOpenInOidcClientAppUrl()} onClick={openAiReportingInNewTab}>
                                    <Icon icon="ai_reporting" />
                                    <span>{t("nav:ai_reporting")}</span>
                                </a>
                            </Menu.Item>
                        )}

                        <Menu.Item key="help" className="opacity-75">
                            <Divider className="mt-4 mb-4" />
                            <a href={HELP_CENTER_LINK} target="_blank" rel="noreferrer">
                                <Icon icon="help" />
                                <span>{t("nav:help_center")}</span>
                            </a>
                        </Menu.Item>
                        <SystemInfo />
                    </div>
                </Menu>
            )}
        </Sider>
    )
}
